import React, { useEffect, useState } from 'react';
import { Alert, Badge, Card, Spinner } from '../../../core/ui';
import { Navigate } from 'react-router-dom';
import { useTranslation } from '../../../core/context/TranslationContext';
import { getFamilyReceipts } from '../api/family';
import { useFamily } from '../context/FamilyContext';
import { memberColor, ownerInitial } from '../format';
import ReceiptDetailPanel from '../../receipts/components/receipt/ReceiptDetailPanel';
import ScopeSwitcher from './ScopeSwitcher';
import SectionedTable from '../../../core/common/SectionedTable';
import axios from '../../../core/api/client';
import styles from './FamilyReceipts.module.css';

export default function FamilyReceipts() {
  const { t } = useTranslation();
  const { family, loading: familyLoading } = useFamily();
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedReceipt, setSelectedReceipt] = useState(null);

  useEffect(() => {
    if (!family) return;
    (async () => {
      try {
        setLoading(true);
        const data = await getFamilyReceipts();
        setReceipts(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err.response?.data?.message || t('error'));
      } finally {
        setLoading(false);
      }
    })();
  }, [family, t]);

  const handleRowClick = async (r) => {
    if (selectedReceipt?.id === r.id) {
      setSelectedReceipt(null);
      return;
    }
    try {
      const res = await axios.get(`/api/receipts/${r.id}`);
      setSelectedReceipt(res.data);
    } catch {
      setSelectedReceipt(null);
    }
  };

  const columns = [
    {
      key: 'date',
      header: t('date'),
      accessor: (r) => r.purchaseDateTime ? new Date(r.purchaseDateTime).getTime() : 0,
      cell: (r) => r.purchaseDateTime ? new Date(r.purchaseDateTime).toLocaleDateString() : '-',
      filterValue: (r) => r.purchaseDateTime ? new Date(r.purchaseDateTime).toLocaleDateString() : '',
    },
    {
      key: 'store',
      header: t('store'),
      accessor: (r) => r.storeName || '',
      cell: (r) => r.storeName || '—',
    },
    {
      key: 'total',
      header: t('total'),
      align: 'end',
      accessor: (r) => Number(r.totalAmount || 0),
      cell: (r) => r.totalAmount != null ? `₺${Number(r.totalAmount).toFixed(2)}` : '-',
      filterable: false,
    },
    {
      key: 'owner',
      header: t('username'),
      accessor: (r) => r.owner?.username || '',
      cell: (r) => (
        <span className={styles.ownerCell}>
          <Badge
            style={{ background: memberColor(r.owner?.username || '') }}
            className={styles.badge}
          >
            {ownerInitial(r.owner?.username)}
          </Badge>
          {r.owner?.username}
        </span>
      ),
    },
  ];

  if (familyLoading) return <Spinner label={t('loading')} className={styles.spinner} />;
  if (!family) return <Navigate to="/family/create" replace />;

  return (
    <div className={styles.container}>
      <Card>
        <Card.Header>
          <div className={styles.cardHeader}>
            <h5 className={styles.cardTitle}>{t('family.receipts')}</h5>
            <ScopeSwitcher
              current="family"
              mineRoute="/receipts"
              familyRoute="/family/receipts"
            />
          </div>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {loading ? (
            <Spinner label={t('loading')} />
          ) : (
            <SectionedTable
              data={receipts}
              columns={columns}
              rowKey={(r) => r.id}
              onRowClick={handleRowClick}
              initialSort={{ key: 'date', dir: 'desc' }}
              emptyMessage={t('noData')}
              enableColumnFilters
            />
          )}
        </Card.Body>
      </Card>

      <ReceiptDetailPanel
        receipt={selectedReceipt}
        readOnly={true}
        onEdit={() => {}}
        onDelete={() => {}}
        onClose={() => setSelectedReceipt(null)}
      />
    </div>
  );
}
